/**
 * The Users list's URL contract — one declaration, and one parser that cannot
 * take the page down.
 *
 * ── WHY THIS IS ITS OWN FILE ──────────────────────────────────────────────
 *
 * Review B2cT2-BF-05. `validateSearch` used to call `schema.parse(...)`
 * directly inside `routes/users/index.tsx`, so an unlisted value THREW inside
 * the router's own validation step and the route rendered its error boundary
 * instead of the roster. That is a live hazard every time a filter's alphabet
 * changes under a URL somebody already has: c32 replaced the employment
 * filter's browser-only vocabulary with the store's six values, and a URL the
 * previous bundle wrote (`?emp=<retired value>` — the roster writes the
 * current filter into the URL on every change, so those URLs exist in history,
 * bookmarks and shared links) took the page down on reload. It is the same
 * class as the stale-bundle proof failure already on record: the tab that
 * wrote the old value is the tab that reloads into the new bundle.
 *
 * A filter is not load-bearing. An unreadable one DEGRADES to its default —
 * the roster renders, unfiltered, which is visibly "we ignored that" — and
 * each field degrades on its own, so one retired value does not drop the other
 * eight filters with it. This is not a fail-open exception to principles.md
 * #4: every one of these values narrows a list the server has already scoped
 * to the tenant and to the caller's permissions, so a dropped filter can only
 * ever show FEWER rows than the caller may read, never more.
 *
 * It lives in `lib/` rather than inside the route so the parser can be tested
 * without mounting the page (`users-list-search.test.ts`), and so the router's
 * file-based generator does not see a non-route module under `routes/`.
 */

import { z } from 'zod';
import { EMPLOYMENT_TYPES } from '@argusai/shared/schemas/user.schema';
import type { UsPersonStanding } from './user-identity-panel';

/**
 * W1a-01: filter state syncs to URL query string so refresh preserves
 * the current view and deep-links work. All keys are optional; absent
 * = default. Unknown keys are stripped by Zod's `.strip()` at parse time.
 *
 * Every closed alphabet carries `.catch(undefined)`: a value this bundle does
 * not know becomes "no filter" instead of a thrown ZodError.
 */
export const usersListSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  status: z
    .enum(['all', 'active', 'invited', 'locked', 'suspended', 'deactivated'])
    .optional()
    .catch(undefined),
  dept: z.string().optional().catch(undefined),
  role: z.string().optional().catch(undefined),
  // B2c-T2 — THE employment-type alphabet, read from the shared declaration.
  // It was a hand-written five-value list whose four types were the browser's
  // own vocabulary, not the store's, so a bookmarked `?emp=vendor` filtered on
  // a value no user row could hold.
  emp: z.enum(['all', ...EMPLOYMENT_TYPES]).optional().catch(undefined),
  clr: z
    .enum(['all', 'unclassified', 'cui', 'secret', 'ts', 'ts_sci'])
    .optional()
    .catch(undefined),
  // USR-F1-T1-R1 — one value per US-person STANDING (US_PERSON_FILTER_STANDING
  // below), so a lapsed verification is its own bucket and never "yes".
  usp: z.enum(['all', 'yes', 'lapsed', 'no']).optional().catch(undefined),
  mfa: z.enum(['all', 'yes', 'no']).optional().catch(undefined),
  rev: z.enum(['all', 'overdue', 'due_soon']).optional().catch(undefined),
});

export type UsersListSearch = z.infer<typeof usersListSearchSchema>;

/** The roster's US-person filter value (`usp`). */
export type UsPersonFilter = NonNullable<UsersListSearch['usp']>;

/**
 * USR-F1-T1-R1 — the standing each US-person filter value matches, read off
 * `usPersonStandingOf` (the one web reader of standing). "yes" is IN STANDING:
 * a verification on file whose re-verification date passed is "lapsed".
 */
export const US_PERSON_FILTER_STANDING: Record<Exclude<UsPersonFilter, 'all'>, UsPersonStanding> = {
  yes: 'in_standing',
  lapsed: 'lapsed',
  no: 'unverified',
};

/**
 * Closes UX-2: the canonical query param is `q`, but external systems (and
 * the API itself) use `search` as the obvious name. Treat `?search=foo` as an
 * alias for `?q=foo` so deep-links don't silently drop the filter. The next
 * URL the UI writes uses `q`, matching the rest of the route's contract.
 */
export function parseUsersListSearch(raw: unknown): UsersListSearch {
  const aliased: Record<string, unknown> = {
    ...((raw as Record<string, unknown> | null | undefined) ?? {}),
  };
  if (
    typeof aliased.search === 'string' &&
    aliased.search.trim() !== '' &&
    (aliased.q == null || aliased.q === '')
  ) {
    aliased.q = aliased.search;
  }
  delete aliased.search;

  // The object itself degrades too: a `raw` that is not an object at all (the
  // router hands us whatever is in the URL) must not throw either.
  const parsed = usersListSearchSchema.safeParse(aliased);
  return parsed.success ? parsed.data : {};
}
